import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get("token");
      const email = searchParams.get("email");

      if (!token || !email) {
        setStatus("error");
        setErrorMessage("Invalid link. Please request a new one.");
        return;
      }

      try {
        const response = await axios.get(`${API}/portal/auth/verify`, {
          params: { token, email },
          withCredentials: true
        });

        if (response.data.success) {
          setStatus("success");
          toast.success("You are signed in!");
          
          // Redirect after a short delay
          setTimeout(() => {
            navigate(response.data.redirect_url || "/portal/build");
          }, 1500);
        }
      } catch (error) {
        setStatus("error");
        setErrorMessage(
          error.response?.data?.detail || "Link verification failed. Please request a new one."
        );
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "verifying" && (
            <>
              <Loader2 size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Verifying your link...</h2>
              <p className="text-muted-foreground mt-2">Just a moment</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-green-700">You are signed in!</h2>
              <p className="text-muted-foreground mt-2">Redirecting you now...</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-red-700">Verification Failed</h2>
              <p className="text-muted-foreground mt-2">{errorMessage}</p>
              <Button 
                className="mt-6"
                onClick={() => navigate("/")}
              >
                Request New Link
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
